// ---------------------------------------------------------------------------
// YouAreYou (uru) — New Trust Link Modal
//
// Single-step flow for creating a direct connection link.
// Redesigned to match ContactDetailModal / TrustLinkSettingsModalV2 visual style.
//
//   Configure state:
//     - Optional recipient input (name, phone, or email)
//     - FactorSettingsPanel: "What I Share" / "What I Ask" (Basic + Enhanced)
//       "What I Ask" theirMode !== "hide" → sets corresponding require_* flag
//     - Button label adapts: "Send Link" if phone/email detected, "Create Link" otherwise
//     - POST /api/uru/connections/create with { config, recipient? }
//
//   Result state (modal body changes in-place):
//     - If connection.sent_via === "sms" | "email": success banner + Done button
//     - Otherwise: success banner + Copy Link + QR Code + Done buttons
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from "react";
import Icon from "@/components/ui/Icon";
import QrShareModal from "@/components/ui/QrShareModal";
import FactorSettingsPanel from "@/components/ui/FactorSettingsPanel";
import type { FactorDef } from "@/components/ui/FactorSettingsPanel";
import {
  createDefaultSettingsV2,
  BASIC_FACTORS,
  ENHANCED_FACTORS,
} from "@/components/ui/FactorSettingsPanel";
import type { TrustLinkSettingsV2, DataShareMode } from "@/components/ui/FactorSettingsPanel";
import { createDirectLink } from "@/api/endpoints";
import { copyToClipboard, detectRecipientType, getInitials } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { resolveAvatarUrl } from "@/types/api";
import type { Connection } from "@/types/api";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface NewTrustLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (connection: Connection) => void;
}

// ---------------------------------------------------------------------------
// Main modal (guard)
// ---------------------------------------------------------------------------

export default function NewTrustLinkModal({
  isOpen, onClose, onCreated,
}: NewTrustLinkModalProps) {
  if (!isOpen) return null;
  return <NewTrustLinkModalInner onClose={onClose} onCreated={onCreated} />;
}

// ---------------------------------------------------------------------------
// Inner modal — mounts fresh each open
// ---------------------------------------------------------------------------

function NewTrustLinkModalInner({
  onClose, onCreated,
}: Omit<NewTrustLinkModalProps, "isOpen">) {
  const { user } = useAuth();

  const myName     = user ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim() || "You" : "You";
  const myInitials = user ? getInitials(user.first_name ?? "", user.last_name ?? "") : "?";
  const myAvatar   = user ? resolveAvatarUrl(user.avatar) : null;
  const myPhone    = user?.phone_number || undefined;
  const myEmail    = user?.email && !user.email.endsWith("@uru.youareyou.io") ? user.email : undefined;

  const [recipient,         setRecipient]         = useState("");
  const [factorSettings,    setFactorSettings]     = useState<TrustLinkSettingsV2>(() => createDefaultSettingsV2());
  const [creating,          setCreating]           = useState(false);
  const [createError,       setCreateError]        = useState<string | null>(null);
  const [createdConnection, setCreatedConnection]  = useState<Connection | null>(null);
  const [copied,            setCopied]             = useState(false);
  const [showQr,            setShowQr]             = useState(false);

  const modalRef        = useRef<HTMLDivElement>(null);
  const recipientRef    = useRef<HTMLInputElement>(null);

  const recipientType = detectRecipientType(recipient);
  const isSendMode    = recipientType === "phone" || recipientType === "email";
  const created       = createdConnection !== null;
  const sentVia       = createdConnection?.sent_via;

  // Scroll lock
  useEffect(() => {
    const orig = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = orig; };
  }, []);

  // Autofocus recipient input
  useEffect(() => {
    const t = setTimeout(() => recipientRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, []);

  // Escape to close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") { e.preventDefault(); onClose(); } };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => { if (e.target === e.currentTarget) onClose(); },
    [onClose],
  );

  // Factor change handlers
  const handleChange = useCallback((key: string, side: "my" | "their", mode: DataShareMode) => {
    setFactorSettings((prev) => ({
      ...prev,
      factors: prev.factors.map((f) =>
        f.key === key ? { ...f, [side === "my" ? "myMode" : "theirMode"]: mode } : f,
      ),
    }));
  }, []);

  const handleBulkChange = useCallback((defs: FactorDef[], side: "my" | "their", mode: DataShareMode) => {
    const keys = new Set(defs.map((d) => d.key));
    setFactorSettings((prev) => ({
      ...prev,
      factors: prev.factors.map((f) => {
        if (!keys.has(f.key)) return f;
        const def = [...BASIC_FACTORS, ...ENHANCED_FACTORS].find((d) => d.key === f.key);
        const effectiveMode = mode === "share" && def?.noShare ? "valid" : mode;
        return { ...f, [side === "my" ? "myMode" : "theirMode"]: effectiveMode };
      }),
    }));
  }, []);

  // Create link — derive require_* flags from theirMode
  const handleCreate = useCallback(async () => {
    setCreating(true);
    setCreateError(null);
    try {
      const fm = Object.fromEntries(factorSettings.factors.map((f) => [f.key, f]));
      const get = (key: string) => fm[key]?.theirMode ?? "hide";

      const config = {
        require_gov_id:    get("gov_id")    !== "hide",
        require_liveness:  get("liveness")  !== "hide",
        require_ssn:       get("ssn")       !== "hide",
        require_ofac:      get("ofac")      !== "hide",
        require_phone:     get("phone")     !== "hide",
        require_email:     get("email")     !== "hide",
        require_address:   get("address")   !== "hide",
        require_signature: get("signature") !== "hide",
        require_dl_back:   get("dl")        !== "hide",
      };

      const trimmed    = recipient.trim();
      const connection = await createDirectLink(config, trimmed || undefined);
      setCreatedConnection(connection);
      onCreated?.(connection);
    } catch (err) {
      setCreateError(
        err instanceof Error ? err.message : "Failed to create link. Please try again.",
      );
    } finally {
      setCreating(false);
    }
  }, [factorSettings, recipient, onCreated]);

  // Copy link
  const handleCopy = useCallback(async () => {
    if (!createdConnection?.link) return;
    const ok = await copyToClipboard(createdConnection.link);
    if (ok) { setCopied(true); setTimeout(() => setCopied(false), 2000); }
  }, [createdConnection]);

  const successText = (() => {
    if (!created) return "";
    const trimmed = recipient.trim();
    if (sentVia) return `Sent to ${trimmed || "recipient"}`;
    if (trimmed) return `Link created for ${trimmed}`;
    return "Link ready to share";
  })();

  return (
    <>
      {createdConnection?.link && (
        <QrShareModal isOpen={showQr} onClose={() => setShowQr(false)}
          url={createdConnection.link} title="Your Trust Link" />
      )}

      <div
        className="fixed inset-0 z-50 flex items-end justify-center md:items-center"
        onClick={handleBackdropClick}
        role="presentation"
      >
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" aria-hidden="true" />

        <div
          ref={modalRef}
          role="dialog" aria-modal="true" aria-labelledby="new-trust-link-title"
          className="relative z-10 flex w-full max-w-[430px] flex-col bg-white shadow-2xl h-full max-h-[100dvh] md:h-auto md:max-h-[90vh] md:max-w-2xl md:rounded-2xl overflow-hidden"
        >
          {/* ── Header — matches ContactDetailModal header exactly ── */}
          <div className="flex shrink-0 items-center border-b border-gray-200 bg-white px-4 py-3">
            <button onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-text-muted hover:bg-gray-100 hover:text-text-primary focus:outline-none"
              aria-label="Close">
              <Icon name="close" className="text-xl" />
            </button>
            <h2 id="new-trust-link-title"
              className="flex-1 pr-8 text-center text-xs font-bold uppercase tracking-widest text-text-secondary">
              New Trust Link
            </h2>
          </div>

          {/* ── Scrollable body ── */}
          <div className="flex-1 overflow-y-auto">
            {!created ? (
              <>
                {/* Recipient input */}
                <div className="px-4 pt-4 pb-2 md:px-6">
                  <label className="flex flex-col gap-1.5">
                    <span className="sr-only">Recipient</span>
                    <input
                      ref={recipientRef}
                      type="text"
                      value={recipient}
                      onChange={(e) => setRecipient(e.target.value)}
                      placeholder="Name, phone, or email (optional)"
                      autoComplete="off"
                      className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-text-primary placeholder:text-text-muted transition-all focus:border-primary-500 focus:bg-white focus:ring-2 focus:ring-primary-500/20 focus:outline-none"
                    />
                  </label>
                  {recipientType === "phone" && (
                    <div className="mt-2 flex items-center gap-1.5 rounded-lg bg-blue-50 px-3 py-2">
                      <Icon name="info" className="text-sm text-blue-500" />
                      <span className="text-xs font-medium text-blue-700">We&apos;ll send them a text message</span>
                    </div>
                  )}
                  {recipientType === "email" && (
                    <div className="mt-2 flex items-center gap-1.5 rounded-lg bg-blue-50 px-3 py-2">
                      <Icon name="info" className="text-sm text-blue-500" />
                      <span className="text-xs font-medium text-blue-700">We&apos;ll send them an email</span>
                    </div>
                  )}
                </div>

                {/* Factor settings panel */}
                <div className="border-t border-gray-100">
                  <FactorSettingsPanel
                    settings={factorSettings}
                    onChange={handleChange}
                    onBulkChange={handleBulkChange}
                    myName={myName}
                    myInitials={myInitials}
                    myAvatarUrl={myAvatar}
                    myData={{ phone: myPhone, email: myEmail }}
                  />
                </div>

                {/* Error */}
                {createError && (
                  <div className="mx-4 mt-2 mb-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700 md:mx-6">
                    {createError}
                  </div>
                )}
              </>
            ) : (
              /* Result body */
              <div className="flex flex-col gap-4 px-4 py-6 pb-40 md:px-6 md:pb-6">
                <div className="flex items-center gap-3 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-100">
                    <Icon name="check" filled className="text-sm text-emerald-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-emerald-800">{successText}</p>
                    {!sentVia && createdConnection?.link && (
                      <p className="truncate text-xs text-emerald-600">
                        {createdConnection.link.replace(/^https?:\/\//, "")}
                      </p>
                    )}
                  </div>
                </div>
                {!sentVia && createdConnection?.link && (
                  <div className="hidden flex-col gap-3 md:flex">
                    <div className="flex gap-3">
                      <button type="button" onClick={handleCopy}
                        className={`flex flex-1 items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-semibold transition-colors focus:outline-none ${
                          copied
                            ? "border border-emerald-200 bg-emerald-50 text-emerald-700"
                            : "bg-primary-500 text-white hover:bg-primary-600"
                        }`}>
                        <Icon name={copied ? "check" : "content_copy"} className="text-base" />
                        {copied ? "Copied!" : "Copy Link"}
                      </button>
                      <button type="button" onClick={() => setShowQr(true)}
                        className="flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3.5 text-sm font-semibold text-text-primary transition-colors hover:bg-gray-50 focus:outline-none"
                        aria-label="Show QR code">
                        <Icon name="qr_code" className="text-base" />
                        QR Code
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Footer ── */}
          {!created ? (
            <div className="shrink-0 border-t border-gray-200 bg-white px-4 py-3 space-y-2">
              <button type="button" onClick={handleCreate} disabled={creating}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary-500 py-3 text-sm font-bold text-white shadow-lg shadow-primary-500/20 transition-all hover:bg-primary-600 active:scale-[0.98] disabled:opacity-60">
                {creating ? (
                  <><div className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />{isSendMode ? "Sending…" : "Creating…"}</>
                ) : (
                  <><Icon name={isSendMode ? "send" : "add_link"} className="text-base" />{isSendMode ? "Send Link" : "Create Link"}</>
                )}
              </button>
              <button type="button" onClick={onClose}
                className="w-full py-2 text-sm font-semibold text-text-muted hover:text-text-primary transition-colors focus:outline-none">
                Cancel
              </button>
            </div>
          ) : (
            <div className="shrink-0 border-t border-gray-200 bg-white px-4 py-3 space-y-2">
              {!sentVia && createdConnection?.link && (
                <div className="flex gap-3">
                  <button type="button" onClick={handleCopy}
                    className={`flex flex-1 items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold transition-colors focus:outline-none ${
                      copied
                        ? "border border-emerald-200 bg-emerald-50 text-emerald-700"
                        : "bg-primary-500 text-white hover:bg-primary-600"
                    }`}>
                    <Icon name={copied ? "check" : "content_copy"} className="text-base" />
                    {copied ? "Copied!" : "Copy Link"}
                  </button>
                  <button type="button" onClick={() => setShowQr(true)}
                    className="flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-text-primary transition-colors hover:bg-gray-50 focus:outline-none"
                    aria-label="Show QR code">
                    <Icon name="qr_code" className="text-base" />
                  </button>
                </div>
              )}
              <button type="button" onClick={onClose}
                className={`flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold transition-all hover:bg-primary-600 active:scale-[0.98] ${
                  sentVia || !createdConnection?.link
                    ? "bg-primary-500 text-white shadow-lg shadow-primary-500/20"
                    : "border border-gray-200 bg-white text-text-primary hover:bg-gray-50"
                }`}>
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
